import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Logo from "../../Images/Logo.svg";
import bgImage from "../../Images/signup-bg.svg";
import RightArrow from "../../Images/Right Arrow.svg";
import Footer from "../LandingPage/Components/Footer";
import ToastMessage from "../../config/ToastMessage";
import { buildApiUrl } from "../../config/api";
import { Eye, EyeOff, Upload } from "lucide-react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "../Studentsign/signup.css";

const Signup = () => {
  const navigate = useNavigate();

  // 🔹 Cloudinary configuration
  const CLOUDINARY_CLOUD_NAME =
    process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || "your_cloud_name";
  const CLOUDINARY_UPLOAD_PRESET =
    process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || "your_upload_preset";
  const CLOUDINARY_URL = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`;

  const [formData, setFormData] = useState({
    first_name: "",
    middle_name: "",
    last_name: "",
    phone: "",
    alternatePhone: "",
    country_code: "",
    gender: "male",
    email: "",
    password: "",
    confirmPassword: "",
    avatar_url: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState({ type: "", message: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  const handleClick = () => navigate("/instructor-signup");

  useEffect(() => {
    if (toast.message) {
      const timer = setTimeout(() => setToast({ message: "" }), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handlePhoneChange = (value, name, country) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      country_code: country?.dialCode || "",
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // 🔹 Upload image to Cloudinary
  const uploadImageToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);
    formData.append("folder", "skillsmd/avatars");

    try {
      const response = await axios.post(CLOUDINARY_URL, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data.secure_url;
    } catch (error) {
      console.error("Cloudinary upload error:", error);
      throw new Error("Failed to upload image");
    }
  };

  // 🔹 Handle file selection and upload
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // ✅ File size validation (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        avatar_url: "Profile picture must be less than 5MB",
      }));
      e.target.value = null;
      return;
    }

    // ✅ File type validation
    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
    if (!validTypes.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        avatar_url: "Only JPG, PNG, and GIF images are allowed",
      }));
      e.target.value = null;
      return;
    }

    // Clear any previous errors
    setErrors((prev) => ({ ...prev, avatar_url: "" }));

    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload to Cloudinary
    setIsUploadingImage(true);
    try {
      const imageUrl = await uploadImageToCloudinary(file);
      setFormData((prev) => ({ ...prev, avatar_url: imageUrl }));
      setToast({
        type: "success",
        message: "Profile picture uploaded successfully!",
      });
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        avatar_url: "Failed to upload image. Please try again.",
      }));
      setImagePreview(null);
      e.target.value = null;
    } finally {
      setIsUploadingImage(false);
    }
  };

  // 🔹 Remove uploaded image
  const handleRemoveImage = () => {
    setFormData((prev) => ({ ...prev, avatar_url: "" }));
    setImagePreview(null);
    setErrors((prev) => ({ ...prev, avatar_url: "" }));
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) fileInput.value = null;
  };

  // ✅ Enhanced validation function
  const validateForm = () => {
    const newErrors = {};

    // Required fields validation
    const requiredFields = [
      "first_name",
      "last_name",
      "phone",
      "gender",
      "email",
      "password",
      "confirmPassword",
    ];

    requiredFields.forEach((field) => {
      if (!formData[field]?.toString().trim()) {
        newErrors[field] = "This field is required";
      }
    });

    // Phone validation
    if (formData.phone && formData.phone.length < 10) {
      newErrors.phone = "Enter a valid phone number (min 10 digits)";
    }

    // ✅ Alternate phone validation (if provided)
    if (formData.alternatePhone && formData.alternatePhone.length < 10) {
      newErrors.alternatePhone =
        "Enter a valid alternate phone number (min 10 digits)";
    }

    // ✅ Improved email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = "Enter a valid email address";
    }

    // ✅ Strong password validation
    if (formData.password) {
      if (formData.password.length < 8) {
        newErrors.password = "Password must be at least 8 characters";
      } else {
        const hasUpperCase = /[A-Z]/.test(formData.password);
        const hasLowerCase = /[a-z]/.test(formData.password);
        const hasNumber = /\d/.test(formData.password);
        const hasSpecialChar = /[@$!%*?&#]/.test(formData.password);

        if (!hasUpperCase || !hasLowerCase || !hasNumber || !hasSpecialChar) {
          newErrors.password =
            "Password must contain uppercase, lowercase, number & special character (@$!%*?&#)";
        }
      }
    }

    // Password match validation
    if (formData.password !== formData.confirmPassword) {
      newErrors.password = "Passwords do not match";
      newErrors.confirmPassword = "Passwords do not match";
    }

    return newErrors;
  };

  // 🔹 Form Submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setToast({
        type: "error",
        message: "Please fix the highlighted fields.",
      });
      // Scroll to first error
      const firstErrorField = Object.keys(validationErrors)[0];
      const element = document.querySelector(`[name="${firstErrorField}"]`);
      element?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setIsLoading(true);

    // 🔹 Now sending JSON instead of FormData
    const payload = {
      full_name:
        `${formData.first_name} ${formData.middle_name} ${formData.last_name}`.trim(),
      first_name: formData.first_name,
      middle_name: formData.middle_name,
      last_name: formData.last_name,
      phone: formData.phone,
      alternate_phone: formData.alternatePhone,
      country_code: formData.country_code,
      gender: formData.gender.toLowerCase(),
      email: formData.email,
      password: formData.password,
      avatar_url: formData.avatar_url,
    };

    try {
      const response = await axios.post(
        buildApiUrl("/user/create_student"),
        payload,
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      if (response?.data?.success) {
        setToast({
          type: "success",
          message: response.data.message || "Account created successfully!",
        });

        // Reset form
        setFormData({
          first_name: "",
          middle_name: "",
          last_name: "",
          phone: "",
          alternatePhone: "",
          country_code: "",
          gender: "",
          email: "",
          password: "",
          confirmPassword: "",
          avatar_url: "",
        });
        setImagePreview(null);
        setErrors({});

        // Redirect to login
        setTimeout(() => navigate("/login"), 3000);
      } else {
        setToast({
          type: "error",
          message: response.data.message || "Something went wrong.",
        });
      }
    } catch (err) {
      // ✅ Simplified error handling
      let errorMessage = "Failed to create account. Please try again.";

      if (err.response?.data) {
        const data = err.response.data;

        // Handle different error response formats
        if (typeof data === "string") {
          errorMessage = data;
        } else if (data.detail) {
          errorMessage = Array.isArray(data.detail)
            ? data.detail.map((e) => e.msg || e).join(", ")
            : data.detail;
        } else if (data.message) {
          errorMessage = data.message;
        } else if (data.error) {
          errorMessage = data.error;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setToast({ type: "error", message: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const inputStyle = (field) =>
    `h-9 px-3 py-2 bg-white rounded shadow-sm outline text-sm text-zinc-700 transition-all ${
      errors[field]
        ? "outline-red-500 ring-1 ring-red-400"
        : "outline-1 outline-zinc-200 focus:outline-2 focus:outline-[#4AACC5]"
    }`;

  return (
    <div
      className="min-h-screen bg-cover bg-center flex flex-col items-center justify-between"
      style={{ backgroundImage: `url(${bgImage})` }}
    >
      {/* Header */}
      <div className="w-full bg-white shadow-sm flex items-center px-6 md:px-10 py-3">
        <img src={Logo} alt="SkillsMD Logo" className="w-32 md:w-36 h-auto" />
      </div>

      {/* Form Card */}
      <div className="relative w-[90%] md:w-[850px] bg-white rounded-2xl border border-zinc-300 shadow-lg p-8 my-8 flex flex-col items-center">
        {toast.message && (
          <div
            className={`absolute top-0 left-0 right-0 w-full z-20 transition-all duration-500 ease-in-out ${
              toast.message
                ? "opacity-100 translate-y-2"
                : "opacity-0 -translate-y-3"
            }`}
          >
            <ToastMessage
              type={toast.type}
              message={toast.message}
              onClose={() => setToast({ message: "" })}
            />
          </div>
        )}

        <h2 className="text-2xl font-bold text-neutral-800 mb-2 text-center">
          Sign Up
        </h2>
        <p className="text-slate-600 text-center text-sm mb-6">
          Create your student account and explore courses right away.
        </p>

        {/* Toggle
        <div className="w-96 h-14 relative mb-6">
          <div className="w-96 h-14 absolute rounded-[84px] border border-zinc-200"></div>
          <div className="absolute left-[18px] top-[7px] w-48 h-10 px-12 py-1.5 inline-flex justify-center items-center bg-cyan-100 rounded-[74px]">
            <div className="text-center text-zinc-800 text-base font-semibold">
              Student
            </div>
          </div>
          <div
            className="absolute left-[190px] top-[8px] w-44 h-10 px-9 py-1.5 inline-flex justify-center items-center cursor-pointer hover:bg-gray-50 rounded-[74px] transition-all"
            onClick={handleClick}
          >
            <div className="text-center text-zinc-800 text-base font-semibold">
              Instructor
            </div>
          </div>
        </div> */}

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="grid grid-cols-1 sm:grid-cols-2 gap-6 w-full max-w-2xl"
        >
          {/* First Name */}
          <div>
            <label className="flex items-start text-sm font-bold text-neutral-800">
              First Name <span className="text-red-600">*</span>
            </label>
            <input
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              type="text"
              placeholder="Enter first name"
              className={`${inputStyle("first_name")} w-full`}
              disabled={isLoading}
            />
            {errors.first_name && (
              <p className="text-red-500 text-xs mt-1">{errors.first_name}</p>
            )}
          </div>

          {/* Middle Name */}
          <div>
            <label className="flex items-start text-sm font-bold text-neutral-800">
              Middle Name
            </label>
            <input
              name="middle_name"
              value={formData.middle_name}
              onChange={handleChange}
              type="text"
              placeholder="Enter middle name"
              className={`${inputStyle("middle_name")} w-full`}
              disabled={isLoading}
            />
          </div>

          {/* Last Name */}
          <div>
            <label className="flex items-start text-sm font-bold text-neutral-800">
              Last Name <span className="text-red-600">*</span>
            </label>
            <input
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              type="text"
              placeholder="Enter last name"
              className={`${inputStyle("last_name")} w-full`}
              disabled={isLoading}
            />
            {errors.last_name && (
              <p className="text-red-500 text-xs mt-1">{errors.last_name}</p>
            )}
          </div>

          {/* Gender */}
          <div>
            <label className="flex items-start text-sm font-bold text-neutral-800">
              Gender <span className="text-red-600">*</span>
            </label>
            <div className="flex items-center gap-4 mt-1">
              {["male", "female"].map((g) => (
                <label
                  key={g}
                  className="flex items-center gap-1 text-sm cursor-pointer"
                >
                  <input
                    type="radio"
                    name="gender"
                    value={g}
                    checked={formData.gender === g}
                    onChange={handleChange}
                    disabled={isLoading}
                    className="cursor-pointer"
                  />
                  {g.charAt(0).toUpperCase() + g.slice(1)}
                </label>
              ))}
            </div>
            {errors.gender && (
              <p className="text-red-500 text-xs mt-1">{errors.gender}</p>
            )}
          </div>

          {/* Mobile */}
          <div>
            <label className="flex items-start text-sm font-bold text-neutral-800">
              Mobile <span className="text-red-600">*</span>
            </label>
            <PhoneInput
              country="us"
              value={formData.phone}
              onChange={(value, country) =>
                handlePhoneChange(value, "phone", country)
              }
              inputProps={{ name: "phone", required: true }}
              placeholder="Enter Mobile"
              separateDialCode={true}
              containerClass="w-full"
              inputClass="!w-full !h-10 !rounded-lg !border !border-gray-300 !text-sm"
              disabled={isLoading}
            />
            {errors.phone && (
              <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
            )}
          </div>

          {/* Alternate Phone */}
          <div>
            <label className="flex items-start text-sm font-bold text-neutral-800">
              Alternate Mobile
            </label>
            <PhoneInput
              country="us"
              value={formData.alternatePhone}
              onChange={(value, country) =>
                handlePhoneChange(value, "alternatePhone", country)
              }
              inputProps={{ name: "alternatePhone" }}
              placeholder="Enter Alternate Mobile"
              separateDialCode={true}
              containerClass="w-full"
              inputClass="!w-full !h-10 !rounded-lg !border !border-gray-300 !text-sm"
              disabled={isLoading}
            />
            {errors.alternatePhone && (
              <p className="text-red-500 text-xs mt-1">
                {errors.alternatePhone}
              </p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="flex items-start text-sm font-bold text-neutral-800">
              Email <span className="text-red-600">*</span>
            </label>
            <input
              name="email"
              value={formData.email}
              onChange={handleChange}
              type="email"
              placeholder="you@example.com"
              className={`${inputStyle("email")} w-full`}
              disabled={isLoading}
            />
            {errors.email && (
              <p className="text-red-500 text-xs mt-1">{errors.email}</p>
            )}
          </div>

          {/* 🔹 Profile Upload with Preview - NOW WITH ERROR DISPLAY */}
          <div className="flex flex-col">
            <label className="flex items-start text-sm font-bold text-neutral-800">
              Profile Picture
            </label>

            {/* Image Preview */}
            {imagePreview && (
              <div className="mt-2 relative w-24 h-24">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="w-full h-full object-cover rounded-lg border-2 border-gray-300"
                />
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  disabled={isUploadingImage || isLoading}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-all"
                >
                  ×
                </button>
              </div>
            )}

            {/* Upload Button */}
            {!imagePreview && (
              <label
                className={`mt-1 flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed rounded-lg cursor-pointer transition-all ${
                  isUploadingImage || isLoading
                    ? "border-gray-300 bg-gray-50 cursor-not-allowed"
                    : errors.avatar_url
                    ? "border-red-500 hover:bg-red-50"
                    : "border-[#4AACC5] hover:bg-cyan-50"
                }`}
              >
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif"
                  onChange={handleFileChange}
                  className="hidden"
                  disabled={isUploadingImage || isLoading}
                />
                {isUploadingImage ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5 text-[#4AACC5]"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    <span className="text-sm text-gray-600">Uploading...</span>
                  </>
                ) : (
                  <>
                    <Upload size={18} className={errors.avatar_url ? "text-red-500" : "text-[#4AACC5]"} />
                    <span className={`text-sm font-medium ${errors.avatar_url ? "text-red-500" : "text-[#4AACC5]"}`}>
                      Choose Image
                    </span>
                  </>
                )}
              </label>
            )}

            {/* 🔹 ERROR MESSAGE - NOW SHOWS BELOW THE FIELD */}
            {errors.avatar_url && (
              <p className="text-red-500 text-xs mt-1">{errors.avatar_url}</p>
            )}

            {/* Helper text - only show if no error */}
            {!errors.avatar_url && (
              <span className="text-xs text-gray-500 mt-1">
                Max size: 5MB (JPG, PNG, GIF)
              </span>
            )}

            {/* Success indicator */}
            {formData.avatar_url && !errors.avatar_url && (
              <span className="text-xs text-green-600 mt-1 flex items-center gap-1">
                ✓ Image uploaded successfully
              </span>
            )}
          </div>

          {/* Password */}
          <div className="relative">
            <label className="flex items-start text-sm font-bold text-neutral-800">
              Password <span className="text-red-600">*</span>
            </label>
            <div className="relative">
              <input
                name="password"
                value={formData.password}
                onChange={handleChange}
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                className={`${inputStyle("password")} w-full pr-10`}
                disabled={isLoading}
              />
              <div
                className="absolute right-3 top-2.5 cursor-pointer text-gray-500 hover:text-[#4AACC5]"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </div>
            </div>
            {errors.password && (
              <p className="text-red-500 text-xs mt-1">{errors.password}</p>
            )}
            {!errors.password && formData.password && (
              <p className="text-gray-500 text-xs mt-1">
                Min 8 chars with uppercase, lowercase, number & special char
              </p>
            )}
          </div>

          {/* Confirm Password */}
          <div className="relative">
            <label className="flex items-start text-sm font-bold text-neutral-800">
              Re-enter Password <span className="text-red-600">*</span>
            </label>
            <div className="relative">
              <input
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Re-enter your password"
                className={`${inputStyle("confirmPassword")} w-full pr-10`}
                disabled={isLoading}
              />
              <div
                className="absolute right-3 top-2.5 cursor-pointer text-gray-500 hover:text-[#4AACC5]"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </div>
            </div>
            {errors.confirmPassword && (
              <p className="text-red-500 text-xs mt-1">
                {errors.confirmPassword}
              </p>
            )}
          </div>

          {/* Submit */}
          <div className="sm:col-span-2 flex flex-col items-center">
            <button
              type="submit"
              disabled={isLoading || isUploadingImage}
              className={`mt-6 w-64 px-5 py-2.5 rounded text-white font-bold text-sm transition-all ${
                isLoading || isUploadingImage
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-[#4AACC5] hover:bg-[#3E95AC]"
              }`}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Signing Up...
                </span>
              ) : (
                "Sign Up"
              )}
            </button>
          </div>
        </form>

        {/* Divider & Login */}
        <div className="flex items-center justify-center gap-3 my-8 w-full">
          <div className="h-px flex-1 bg-zinc-200"></div>
          <span className="text-neutral-600 text-xs font-medium">
            Or continue with
          </span>
          <div className="h-px flex-1 bg-zinc-200"></div>
        </div>

        <button
          onClick={() => navigate("/login")}
          disabled={isLoading}
          className={`w-72 flex items-center justify-center gap-2 px-5 py-2.5 bg-white rounded-lg outline outline-1 outline-[#4AACC5] text-[#4AACC5] font-semibold text-sm transition-all duration-300 ${
            isLoading ? "opacity-50 cursor-not-allowed" : "hover:bg-[#E8F6F9]"
          }`}
        >
          Log In
          <img src={RightArrow} alt="Right Arrow" className="w-4 h-4" />
        </button>
      </div>

      <Footer />
    </div>
  );
};

export default Signup;
